import { useCallback, useEffect, useState } from "react";
import { api, money, formatDate, errMsg, todayStr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/Stat";
import { DataTable } from "@/components/DataTable";
import { FormDialog } from "@/components/FormDialog";
import { TxPanel, sourceLabel } from "@/components/TxPanel";
import { toast } from "sonner";

const AREAS = ["Genel", "Otomotiv", "İnşaat", "Hafriyat"];

function Filter({ value, onChange, options, all, testId, w = "w-40" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={w} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">{all}</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function Finance() {
  const { can } = useAuth();
  const [area, setArea] = useState("all");
  const [direction, setDirection] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [q, setQ] = useState("");
  const [tick, setTick] = useState(0);
  const params = { area, direction, start: start || undefined, end: end || undefined, open_only: openOnly || undefined, q: q || undefined };

  return (
    <div className="space-y-6" data-testid="finance-page">
      <PageHeader title="Finans & Ödemeler" subtitle="Tüm iş alanlarının merkezi finansal hareketleri — her tutar tek bir kaynak kayda bağlıdır">
        {can("finans") && ["Gider", "Gelir"].map((dir) => (
          <FormDialog key={dir} title={`Genel ${dir} Kaydı`} triggerLabel={`Genel ${dir}`} triggerVariant={dir === "Gelir" ? "outline" : "default"}
            triggerTestId={`add-general-${dir === "Gelir" ? "income" : "expense"}-btn`} url="/transactions" successMsg="Kaydedildi"
            onDone={() => setTick((t) => t + 1)} initial={{ direction: dir, transaction_date: todayStr() }}
            fields={[{ name: "category_id", label: "Kategori", type: "entity", endpoint: "/categories", params: { type: dir }, required: true,
              labelKey: (c) => `${c.business_area} · ${c.name}` },
              { name: "amount", label: "Tutar (₺)", type: "number", required: true }, { name: "description", label: "Açıklama", required: true },
              { name: "transaction_date", label: "İşlem Tarihi", type: "date" }, { name: "due_date", label: "Vade", type: "date" },
              { name: dir === "Gelir" ? "customer_id" : "supplier_id", label: dir === "Gelir" ? "Müşteri" : "Tedarikçi", type: "entity", endpoint: dir === "Gelir" ? "/customers" : "/suppliers" },
              { name: "employee_id", label: "Personel", type: "entity", endpoint: "/employees" },
              { name: "paid_now", label: "Ödendi olarak kaydet", type: "checkbox" }]} />
        ))}
      </PageHeader>
      <Tabs defaultValue="ledger">
        <TabsList>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Finansal Hareketler</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Ödeme / Tahsilat Kayıtları</TabsTrigger>
        </TabsList>
        <TabsContent value="ledger">
          <Card className="p-4 space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <Input placeholder="Açıklamada ara..." className="w-56" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tx-search" />
              <Filter value={area} onChange={setArea} options={AREAS} all="Tüm Alanlar" testId="tx-area-filter" />
              <Filter value={direction} onChange={setDirection} options={["Gelir", "Gider"]} all="Gelir + Gider" testId="tx-direction-filter" />
              <Input type="date" className="w-40" value={start} onChange={(e) => setStart(e.target.value)} data-testid="tx-start" />
              <Input type="date" className="w-40" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="tx-end" />
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={openOnly} onCheckedChange={(c) => setOpenOnly(!!c)} data-testid="tx-open-only" />Sadece açık bakiyeler</label>
            </div>
            <TxPanel params={params} reloadKey={tick} />
          </Card>
        </TabsContent>
        <TabsContent value="payments"><Payments /></TabsContent>
      </Tabs>
    </div>
  );
}

function Payments() {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [type, setType] = useState("all");
  const [area, setArea] = useState("all");
  const [status, setStatus] = useState("all");
  const load = useCallback(() => {
    api.get("/payments", { params: { payment_type: type, area, status } }).then((r) => setData(r.data));
  }, [type, area, status]);
  useEffect(() => { load(); }, [load]);

  const update = async (p, st) => {
    const send = (confirm) => api.patch(`/payments/${p.id}`, { status: st, confirm_overpay: confirm });
    try {
      try { await send(false); } catch (e) {
        if (e.response?.status === 409 && window.confirm(`${errMsg(e)}\n\nDevam edilsin mi?`)) await send(true); else throw e;
      }
      toast.success(st === "Ödendi" ? "Ödendi olarak işaretlendi" : "Ödeme iptal edildi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card className="p-4 space-y-4">
      <div className="flex flex-wrap gap-2">
        <Filter value={type} onChange={setType} options={["Tahsilat", "Ödeme"]} all="Tüm Türler" testId="pay-type-filter" />
        <Filter value={area} onChange={setArea} options={AREAS} all="Tüm Alanlar" testId="pay-area-filter" />
        <Filter value={status} onChange={setStatus} options={["Ödendi", "Bekliyor", "Gecikmiş", "İptal"]} all="Tüm Durumlar" testId="pay-status-filter" />
        <span className="text-sm text-muted-foreground self-center">{data.total} kayıt</span>
      </div>
      <DataTable testIdPrefix="payment-row" rows={data.items} empty="Ödeme kaydı yok" columns={[
        { key: "payment_date", label: "Ödeme Tarihi", render: (p) => formatDate(p.payment_date) },
        { key: "due_date", label: "Vade", render: (p) => formatDate(p.due_date) },
        { key: "payment_type", label: "Tür", render: (p) => <StatusBadge value={p.payment_type === "Tahsilat" ? "Gelir" : "Gider"} /> },
        { key: "related_entity_type", label: "Kaynak", render: (p) => <span className="text-xs">{sourceLabel(p.related_entity_type)}</span> },
        { key: "description", label: "Açıklama", className: "max-w-[260px] truncate" },
        { key: "party_name", label: "Cari" }, { key: "payment_method", label: "Yöntem" },
        { key: "amount", label: "Tutar", align: "right", render: (p) => <b>{money(p.amount)}</b> },
        { key: "status", label: "Durum", render: (p) => <StatusBadge value={p.status} /> },
        { key: "a", label: "", render: (p) => can("finans") && p.status !== "İptal" && (
          <div className="flex gap-1 justify-end">
            {p.status !== "Ödendi" && <Button size="sm" variant="outline" onClick={() => update(p, "Ödendi")} data-testid={`mark-paid-${p.id}`}>Ödendi</Button>}
            <Button size="sm" variant="ghost" onClick={() => window.confirm("Ödeme iptal edilsin mi?") && update(p, "İptal")} data-testid={`cancel-payment-${p.id}`}>İptal</Button>
          </div>) },
      ]} />
    </Card>
  );
}
